import { BaseClickable, Box, Icons, NavBar, SolvTheme, Stack } from '@solvhealth/jigsaw-v1';
import Booking from '@solvhealth/types/interfaces/Booking';
import Location from '@solvhealth/types/interfaces/Location';
import React, { PureComponent } from 'react';
import { booking } from 'stories/fixtures/booking';
import ReviewHeader from '~/components/AddReview/ReviewHeader';
import BorderBoxReset from '~/components/util/BorderBoxReset';
import { formatLocationName } from '~/core/util/location';
import PrimaryRatingSelector, {
  PRIMARY_RATING,
} from '../../../components/LeaveAReview/components/PrimaryRatingSelector';
import RatingDescriptionForm from '../../../components/LeaveAReview/components/RatingDescriptionForm';
import SecondaryStarSelectors from '../../../components/LeaveAReview/components/SecondaryStarSelectors';
import {
  AUDIO_VIDEO_QUALITY,
  BEDSIDE_MANNER,
  CONSENT_FIELD,
  CONSENT_NAME,
  CONSENT_VISIT_TYPE,
  FACILITY_CLEANLINESS,
  OFFICE_ENVIRONMENT,
  QUALITY_OF_CARE,
  RATING_DESCRIPTION_FIELD,
  STAFF_FRIENDLINESS,
  STAFF_FRIENDLINESS_OPTIONAL,
  WAIT_TIME,
} from '../../../components/LeaveAReview/constants';
import { ADD_REVIEW_TO_SOLV } from '../../../constants/index';
import { analyticsTrackEvent } from '../../../core/analytics';
import {
  ADD_REVIEW_ON_SOLV_CLICK_OVERALL_RATING,
  ADD_REVIEW_SUBMIT,
} from '../../../core/analytics/events';
import AddReviewWarning from '../AddReviewWarning';
import FlowTransition from '../transitions/FlowTransition';

type Props = {
  bookingId: string;
  booking: Booking;
  location: Location;
  handleRatingChange: (...args: any[]) => any;
  error?: any;
  flowState: string;
  submitReview: (...args: any[]) => any;
  flowType: string;
  leaveAReview: any;
  onStarChange: (...args: any[]) => any;
  secondaryRatings?: string[];
  starRating?: number;
};

class ReviewOnSolvCollector extends PureComponent<Props> {
  handleReviewSubmit = (form: any) => {
    const formData = {
      ...form,
      ...this.props.leaveAReview,
      bookingId: this.props.bookingId,
      locationId: this.props.location.id,
    };

    const formattedData = {
      starRating: formData[PRIMARY_RATING],
      review: formData[RATING_DESCRIPTION_FIELD],
      consentToPublish: formData[CONSENT_FIELD],
      hasConsentedToPublishPatientName: formData[CONSENT_NAME],
      hasConsentedToPublishVisitType: formData[CONSENT_VISIT_TYPE],
      ratingWaitTime: formData[WAIT_TIME],
      ratingBedsideManner: formData[BEDSIDE_MANNER],
      ratingStaffFriendliness:
        formData[STAFF_FRIENDLINESS] || formData[STAFF_FRIENDLINESS_OPTIONAL],
      ratingQualityOfCare: formData[QUALITY_OF_CARE],
      ratingFacilityCleanliness: formData[FACILITY_CLEANLINESS] || formData[OFFICE_ENVIRONMENT],
      ratingAudioVideoQuality: formData[AUDIO_VIDEO_QUALITY],
      locationId: formData.locationId,
      bookingId: formData.bookingId,
      providerId: this.props.booking.providerId,
    };

    analyticsTrackEvent(ADD_REVIEW_SUBMIT, {
      ...formattedData,
      flowType: this.props.flowType,
    });

    this.props.submitReview(formattedData);
  };

  handlePrimaryStarChange = (updateObj: any) => {
    analyticsTrackEvent(ADD_REVIEW_ON_SOLV_CLICK_OVERALL_RATING, {
      bookingId: this.props.bookingId,
      locationId: this.props.location.id,
      flowType: this.props.flowType,
      rating: updateObj.value,
    });

    this.props.onStarChange(updateObj);

    this.props.handleRatingChange(updateObj.value);
  };

  renderCollectReviews = () => {
    const { location, leaveAReview, onStarChange, secondaryRatings, flowState } = this.props;

    return (
      <BorderBoxReset>
        <Stack data-testid="review-on-solv-collector">
          <ReviewHeader booking={this.props.booking} location={location} reviewStatus={flowState} />
          <AddReviewWarning error={this.props.error} ratingDate={this.props.booking?.ratingDate} />

          <Stack py={4} space={3} textAlign="left">
            <Stack
              maxWidth={SolvTheme.breakpoints.sm}
              mx="auto"
              px={3}
              py={3}
              space={3}
              width="100%"
            >
              <PrimaryRatingSelector
                leaveAReview={leaveAReview}
                onChange={this.handlePrimaryStarChange}
              />

              <SecondaryStarSelectors
                leaveAReview={leaveAReview}
                location={location}
                onChange={onStarChange}
                ratingQuestions={secondaryRatings}
              />

              <RatingDescriptionForm
                leaveAReview={leaveAReview}
                location={location}
                submit={this.handleReviewSubmit}
              />
            </Stack>
          </Stack>
        </Stack>
      </BorderBoxReset>
    );
  };

  render() {
    return (
      <FlowTransition
        flowState={this.props.flowState}
        stateToActivateFlow={ADD_REVIEW_TO_SOLV}
        toRender={this.renderCollectReviews}
      />
    );
  }
}

export default ReviewOnSolvCollector;
